import { ChartData, ChartOptions } from 'chart.js';
import _, { isEqual } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Radar from '../../../../../components/charts/Radar';
import { Theme } from '../../../../../theme';
import { ChartSkill } from './types';
import {
  buildSkillGroups,
  computeGroupAverage,
  formatAverage,
  SkillGroupBy,
} from '../common/skillGrouping';

type Props = {
  skills: ChartSkill[];
  grouping?: SkillGroupBy;
  resolveSkillName?: (skillId: string) => string | undefined;
};

type Spoke = {
  name: string;
  value: number;
  tooltipHtml: string;
};

// The shared Dashkit chart config renders each tooltip line via innerHTML with
// `white-space: nowrap`, so we escape dynamic text and join lines with <br> to
// get a compact multi-line tooltip that doesn't overflow the card.
const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const SkillsRadarChartComponent: React.FC<React.PropsWithChildren<Props>> = ({
  skills,
  grouping = 'skill',
  resolveSkillName,
}) => {
  const { t } = useTranslation();
  const legend = t('skills.employeeSkills.chart.legend');

  const spokes: Spoke[] = useMemo(() => {
    if (grouping === 'skill') {
      return skills.map((s) => ({
        name: s.name,
        value: s.level ?? 0,
        tooltipHtml: escapeHtml(`${legend} ${s.level ?? 0}`),
      }));
    }
    const skillById = new Map(skills.map((s) => [s.id, s]));
    return buildSkillGroups(skills, grouping, (id) =>
      resolveSkillName?.(id),
    ).map((group) => {
      const avg = computeGroupAverage(
        group.skillIds,
        (id) => skillById.get(id)?.level,
        (id) => skillById.get(id)?.name ?? id,
      );
      const tooltipHtml =
        avg.average === null
          ? '–'
          : [
              escapeHtml(
                t('skills.grouping.averageValue', {
                  average: formatAverage(avg.average),
                }),
              ),
              `<strong style="display:inline-block;margin-top:0.4rem">${escapeHtml(
                t('skills.grouping.skillsHeader'),
              )}</strong>`,
              ...avg.contributions.map((c) =>
                escapeHtml(`${c.name}: ${c.level}`),
              ),
            ].join('<br>');
      return {
        name: group.name,
        value: avg.average ?? 0,
        tooltipHtml,
      };
    });
  }, [skills, grouping, resolveSkillName, legend, t]);

  return (
    <Radar data={getChartData(spokes)} options={getChartOptions(spokes)} />
  );
};

export const SkillsRadarChart = React.memo(
  SkillsRadarChartComponent,
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);

const getChartData = (spokes: Spoke[]): ChartData<'radar'> => {
  return {
    labels: spokes.map((s) => s.name),
    datasets: [
      {
        data: spokes.map((s) => s.value),
      },
    ],
  };
};

const theme = Theme.chart.skills;
const getChartOptions = (spokes: Spoke[]): ChartOptions<'radar'> => {
  return {
    elements: {
      line: {
        tension: 0,
        borderWidth: 1,
        borderColor: theme.line.borderColor,
        backgroundColor: theme.line.backgroundColor,
      },
      point: {
        radius: 2,
        pointStyle: 'circle',
        backgroundColor: theme.point.color,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 5,
        angleLines: {
          color: theme.gridLines.color,
        },
        ticks: {
          stepSize: 1,
          color: theme.ticks.color,
        },
        grid: {
          color: theme.gridLines.color,
        },
        pointLabels: {
          color: theme.labels.color,
          callback: (tooltipItem) => {
            return _.truncate(tooltipItem, { length: 16 });
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            return spokes[context.dataIndex]?.tooltipHtml ?? '';
          },
          // Hide the shared tooltip's colour dot — it reads as noise here.
          labelColor: () => ({
            backgroundColor: 'transparent',
            borderColor: 'transparent',
          }),
        },
      },
    },
  };
};
